// App.js
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

import Layout from "./components/Layout";
import { buildApiUrl } from "./config/api";
import { defaultThemeId, getThemeById, isThemeTestingEnabled } from "./config/themes";
import LeagueSummary from "./pages/LeagueSummary";
import Record from "./pages/Records";
import ModernizePreview from "./pages/ModernizePreview";

const SEED = window.__INITIAL_DATA__ || null;

function fetchLiveData(path) {
  return fetch(buildApiUrl(path), { cache: "no-store" }).then(response => {
    if (!response.ok) {
      throw new Error(`Fetch ${path} failed: HTTP ${response.status}`);
    }
    return response.json();
  });
}

export default function App() {
  const [activeThemeId, setActiveThemeId] = useState(() => {
    if (!isThemeTestingEnabled) {
      return defaultThemeId;
    }

    const urlThemeId = new URLSearchParams(window.location.search).get("theme");
    return urlThemeId || window.localStorage.getItem("sunday-ledger-theme") || defaultThemeId;
  });
  const [stats, setStats] = useState(() => SEED ? (SEED.summary.data || []) : []);
  const [rawStats, setRawStats] = useState(() => SEED ? (Array.isArray(SEED.raw.data) ? SEED.raw.data : SEED.raw) : []);
  const [records, setRecords] = useState(() => SEED ? (Array.isArray(SEED.records.data) ? SEED.records.data : []) : []);
  const [rivalries, setRivalries] = useState(() => SEED?.rivalries ? (Array.isArray(SEED.rivalries.data) ? SEED.rivalries.data : SEED.rivalries) : []);
  const [statsLoading, setStatsLoading] = useState(!SEED);
  const activeTheme = getThemeById(activeThemeId);

  useEffect(() => {
    if (!isThemeTestingEnabled) {
      const url = new URL(window.location.href);
      if (url.searchParams.has("theme")) {
        url.searchParams.delete("theme");
        window.history.replaceState({}, "", url);
      }
      window.localStorage.removeItem("sunday-ledger-theme");
      return;
    }

    window.localStorage.setItem("sunday-ledger-theme", activeTheme.id);
    const url = new URL(window.location.href);
    url.searchParams.set("theme", activeTheme.id);
    window.history.replaceState({}, "", url);
  }, [activeTheme]);

  useEffect(() => {
    fetchLiveData("/league-summary")
      .then(data => setStats(data.data || []))
      .catch(err => console.error("Summary fetch error:", err))
      .finally(() => setStatsLoading(false));

    fetchLiveData("/raw-data")
      .then(data => setRawStats(Array.isArray(data.data) ? data.data : data))
      .catch(err => console.error("Raw data fetch error:", err));

    fetchLiveData("/records")
      .then(data => setRecords(Array.isArray(data.data) ? data.data : []))
      .catch(err => console.error("Records fetch error:", err));

    fetchLiveData("/rivalries")
      .then(data => setRivalries(Array.isArray(data.data) ? data.data : []))
      .catch(err => console.error("Rivalries fetch error:", err));
  }, []);

  return (
    <Router>
      <Routes>
        <Route
          element={
            <Layout
              theme={activeTheme}
              onThemeChange={isThemeTestingEnabled ? setActiveThemeId : undefined}
              themeTestingEnabled={isThemeTestingEnabled}
            />
          }
        >
          <Route path="/" element={<LeagueSummary stats={stats} rawData={rawStats} theme={activeTheme} loading={statsLoading} />} />
          <Route path="/league-lab" element={<ModernizePreview stats={stats} rawData={rawStats} rivalries={rivalries} loading={statsLoading} />} />
          <Route path="/modernize" element={<ModernizePreview stats={stats} rawData={rawStats} rivalries={rivalries} loading={statsLoading} />} />
          <Route path="/records" element={<Record initialRecords={records} theme={activeTheme} />} />
        </Route>
      </Routes>
    </Router>
  );
}
